import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/empty-state.default.js';
import * as wireframeSkin from './skins/empty-state.wireframe.js';

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

export const EMPTY_STATE_SIZES = Object.freeze(['sm', 'md', 'lg']);
export const EMPTY_STATE_VARIANTS = Object.freeze(['vertical', 'horizontal']);
export const EMPTY_STATE_TONES = Object.freeze(['neutral', 'subtle']);
export const EMPTY_STATE_HEADING_LEVELS = Object.freeze([2, 3, 4, 5, 6]);

class RunEmptyState extends RunElement {
  #size = 'md';
  #variant = 'vertical';
  #tone = 'neutral';
  #headingLevel = 2;
  #rendering = false;
  _mediaSlotted = false;
  _iconSlotted = false;

  static get observedAttributes() {
    return ['size', 'variant', 'tone', 'heading-level'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'status');
    this.#wireSlotObservers();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'size':          this.size = newValue; break;
      case 'variant':       this.variant = newValue; break;
      case 'tone':          this.tone = newValue; break;
      case 'heading-level': this.headingLevel = newValue; break;
    }
  }

  get size() { return this.#size; }
  set size(value) {
    const coerced = EMPTY_STATE_SIZES.includes(String(value ?? '').trim())
      ? String(value).trim() : 'md';
    const propMatch = this.#size === coerced;
    if (propMatch && this.getAttribute('size') === coerced) return;
    this.#size = coerced;
    this.setAttribute('size', coerced);
    if (!propMatch) this.requestUpdate();
  }

  get variant() { return this.#variant; }
  set variant(value) {
    const coerced = EMPTY_STATE_VARIANTS.includes(String(value ?? '').trim())
      ? String(value).trim() : 'vertical';
    const propMatch = this.#variant === coerced;
    if (propMatch && this.getAttribute('variant') === coerced) return;
    this.#variant = coerced;
    this.setAttribute('variant', coerced);
    if (!propMatch) this.requestUpdate();
  }

  get tone() { return this.#tone; }
  set tone(value) {
    const coerced = EMPTY_STATE_TONES.includes(String(value ?? '').trim())
      ? String(value).trim() : 'neutral';
    const propMatch = this.#tone === coerced;
    if (propMatch && this.getAttribute('tone') === coerced) return;
    this.#tone = coerced;
    this.setAttribute('tone', coerced);
    if (!propMatch) this.requestUpdate();
  }

  get headingLevel() { return this.#headingLevel; }
  set headingLevel(value) {
    const n = parseInt(value, 10);
    const coerced = EMPTY_STATE_HEADING_LEVELS.includes(n) ? n : 2;
    const propMatch = this.#headingLevel === coerced;
    if (propMatch && this.getAttribute('heading-level') === String(coerced)) return;
    this.#headingLevel = coerced;
    this.setAttribute('heading-level', String(coerced));
    if (!propMatch) this.requestUpdate();
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skin = this.#resolveSkin();
      if (skin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
  }

  #wireSlotObservers() {
    const mediaSlot = this.shadowRoot?.querySelector('slot[name="media"]');
    const iconSlot  = this.shadowRoot?.querySelector('slot[name="icon"]');
    if (mediaSlot) {
      mediaSlot.addEventListener('slotchange', () => {
        this._mediaSlotted = mediaSlot.assignedNodes({ flatten: true }).length > 0;
        this.requestUpdate();
      });
    }
    if (iconSlot) {
      iconSlot.addEventListener('slotchange', () => {
        this._iconSlotted = iconSlot.assignedNodes({ flatten: true }).length > 0;
        this.requestUpdate();
      });
    }
  }

  #resolveSkin() {
    const instanceSkin = this.getAttribute('skin');
    if (instanceSkin) return instanceSkin;
    return resolveSkinFromAncestors(this);
  }

  #updateAria() {
    this.setAttribute('aria-live', 'polite');
  }
}

if (!customElements.get('run-empty-state')) {
  customElements.define('run-empty-state', RunEmptyState);
}

export { RunEmptyState };
export default RunEmptyState;
